import React from 'react';
import classnames from 'classnames';
import { Spinner } from '../../../shared/components/spinner/spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonIconName = 'heart' | 'heart-filled';

interface ButtonBaseProps {
    variant?: ButtonVariant;
    size?: ButtonSize;
    isLoading?: boolean;
    className?: string;
    iconName?: ButtonIconName;
}

interface ButtonAsButtonProps extends ButtonBaseProps, React.ButtonHTMLAttributes<HTMLButtonElement> {
    children: React.ReactNode;
    mode?: 'button';
    href?: never;
}

interface ButtonAsAnchorProps extends ButtonBaseProps, React.AnchorHTMLAttributes<HTMLAnchorElement> {
    children: React.ReactNode;
    /** Renders an anchor styled as a button. Requires href. */
    mode: 'anchor';
    href: string | undefined;
}

type ButtonProps = ButtonAsButtonProps | ButtonAsAnchorProps;

export default function Button({
    children,
    className = '',
    isLoading = false,
    variant = 'primary',
    iconName,
    mode,
    ...props
}: ButtonProps) {
    const buttonClassName = classnames(className, 'bs-button-component', `bs-button-component--${variant}`, {
        [`bs-button-component--icon-${iconName}`]: !!iconName && !isLoading,
        'bs-button-component--loading': isLoading
    });
    const content = isLoading ? <Spinner /> : children;

    if (mode === 'anchor') {
        const isDisabled = props.href === undefined || props.href === '';
        return (
            <a className={buttonClassName} {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)} aria-disabled={isDisabled}>
                {content}
            </a>
        );
    }

    return (
        <button type='button' className={buttonClassName} {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}>
            {content}
        </button>
    );
}
